import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrencies } from '@percent/admin-dashboard/common/hooks'
import { formatAmountFromMinorUnits } from '@percent/admin-dashboard/common/utility/formatAmount/formatAmount'
import { StatusCard } from './statusCard/StatusCard'
import { STATUS_OVERVIEW_STATUSES, StatusOverviewProps, StatusOverviewStatus } from './StatusOverview.types'
import style from './StatusOverview.module.scss'

export function StatusOverview({ counts, totals, showReturned }: Readonly<StatusOverviewProps>) {
  const { t } = useTranslation()
  const { currencyInfo } = useCurrencies()

  const nameFromStatus: Record<StatusOverviewStatus, string> = {
    total: t('typography.total'),
    trusteeApprovalRequired: t('status.foundationApproval'),
    agentApprovalRequired: t('status.agentApproval'),
    managerApprovalRequired: t('status.managerApproval'),
    inTransit: t('status.sent'),
    paid: t('status.paid'),
    returned: t('status.returned'),
    failed: t('status.failed'),
    cancelled: t('status.removed')
  }

  // Payment-initiated disbursements are on their way to the organisation, so they
  // belong under Sent alongside the ones already in transit.
  const countByStatus: Record<StatusOverviewStatus, number> = {
    total: counts.total,
    trusteeApprovalRequired: counts.trusteeApprovalRequired,
    agentApprovalRequired: counts.agentApprovalRequired,
    managerApprovalRequired: counts.managerApprovalRequired,
    inTransit: counts.inTransit + (counts.paymentInitiated ?? 0),
    paid: counts.paid,
    returned: counts.returned ?? 0,
    failed: counts.failed,
    cancelled: counts.cancelled
  }

  const amountByStatus: Record<StatusOverviewStatus, number> = {
    total: totals.total,
    trusteeApprovalRequired: totals.trusteeApprovalRequired,
    agentApprovalRequired: totals.agentApprovalRequired,
    managerApprovalRequired: totals.managerApprovalRequired,
    inTransit: totals.inTransit + (totals.paymentInitiated ?? 0),
    paid: totals.paid,
    returned: totals.returned ?? 0,
    failed: totals.failed,
    cancelled: totals.cancelled
  }

  // Returned and Failed only appear once a payment has actually been returned or
  // failed, so batches that never hit either don't carry permanently empty cards.
  const orderedStatuses = useMemo<StatusOverviewStatus[]>(
    () =>
      STATUS_OVERVIEW_STATUSES.filter(status => {
        if (status === 'returned') {
          return showReturned && countByStatus.returned > 0
        }

        if (status === 'failed') {
          return countByStatus.failed > 0
        }

        return true
      }),
    [showReturned, countByStatus.returned, countByStatus.failed]
  )

  return (
    <div className={style.row} data-testid="status-overview">
      {orderedStatuses.map(status => (
        <StatusCard
          key={status}
          status={status}
          label={nameFromStatus[status]}
          count={countByStatus[status]}
          amount={formatAmountFromMinorUnits({
            value: amountByStatus[status],
            currencyCode: totals.currencyCode,
            currencies: currencyInfo
          })}
        />
      ))}
    </div>
  )
}
